import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { clearAuthSession } from "@/lib/authSession";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
    } catch (err) {
      // 401 is expected when not logged in — surface anything else
      if (err?.response?.status && err.response.status !== 401) {
        console.warn("[Auth] /auth/me failed:", err.response.status, err.response.data);
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    const onProfileUpdated = (event) => {
      const nextName = event?.detail?.name;
      if (!nextName) return;
      setUser((prev) => (prev ? { ...prev, name: nextName } : prev));
    };
    window.addEventListener("factlify:profile-updated", onProfileUpdated);
    return () => window.removeEventListener("factlify:profile-updated", onProfileUpdated);
  }, []);

  useEffect(() => {
    const handle = () => { void checkAuth(); };
    window.addEventListener("factlify:needs-reauth", handle);
    return () => window.removeEventListener("factlify:needs-reauth", handle);
  }, [checkAuth]);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      console.warn("[Auth] logout request failed:", err?.message);
    }
    clearAuthSession();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
